import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { Auth, Amplify } from 'aws-amplify';
import './App.css';

import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
// import Historico from './components/History/Historico';
import Anamnese from './components/Anamnese/Anamnese';
import Pacientes from './components/Pacientes/Pacientes';
import BookingCreatePage from './components/Booking/BookingCreatePage';
import BookingViewPage from './components/Booking/BookingViewPage';
import BlockingPage from './components/Booking/BlockingPage';
import Config from './components/Config/Config';
import Financeiro from './components/Financeiro/Financeiro';
import CashFlow from './components/Financeiro/CashFlow';
import Estoque from './components/Estoque/Estoque';
import Reports from './components/Reports/Reports';
import AgendamentosReportPage from './components/Reports/AgendamentosReportPage';
import ReportPage from './components/Financeiro/ReportPage';
import Prontuario from './components/Prontuario/Prontuario';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import { ROUTE_PERMISSIONS } from './config/permissions';
import Users from './components/Users/Users';

Amplify.configure({
  Auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_vbA0WoIfI',
    userPoolWebClientId: '1mf0d541j8u21c47761cjcsv47',
    identityPoolId: null,
    oauth: {
      // domain: 'login.doctor.systems',
      domain: 'login.app.nexusclinica.com',
      lang: 'pt-BR',
      scope: ['email', 'profile', 'openid'],
      redirectSignIn: `${window.location.origin}`,
      redirectSignOut: `${window.location.origin}`,
      responseType: 'code'
    },
  },
});

// Wrapper component to handle route persistence
function AppContent() {
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [initialPathRestored, setInitialPathRestored] = useState(false);

  // Save current path when location changes
  useEffect(() => {
    if (user && location.pathname !== '/' && !initialPathRestored) {
      // console.log('Saving path:', location.pathname);
      localStorage.setItem('lastPath', location.pathname);
    }
  }, [location, user, initialPathRestored]);

  // Dedicated effect to restore the path after authentication
  useEffect(() => {
    if (user && !initialPathRestored) {
      const savedPath = localStorage.getItem('lastPath');
      // console.log('Found saved path:', savedPath);
      
      if (savedPath && savedPath !== '/' && location.pathname === '/') {
        // console.log('Navigating to saved path:', savedPath);
        // Use replace to avoid creating a history entry
        navigate(savedPath, { replace: true });
      }
      setInitialPathRestored(true);
    }
  }, [user, navigate, location.pathname, initialPathRestored]);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const userData = await Auth.currentAuthenticatedUser();
      setUser(userData);
      setIsLoading(false);
    } catch (err) {
      setUser(null);
      setIsLoading(false);
      
      // Save current path before redirecting to login
      if (location.pathname !== '/') {
        // console.log('Saving path before login redirect:', location.pathname);
        localStorage.setItem('lastPath', location.pathname);
      }
      
      // Get the current Amplify Auth configuration
      const config = Auth.configure();
      const { domain } = config.oauth;
      const clientId = config.userPoolWebClientId;
      
      // Build the auth URL with language parameter
      const redirectUri = encodeURIComponent(window.location.origin);
      const scope = 'email+profile+openid';
      const responseType = 'code';
      
      // Create URL with language parameter
      const authUrl = `https://${domain}/oauth2/authorize?response_type=${responseType}&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&lang=pt-BR`;
      // console.log("Entered the checkuser");
      
      // Redirect to the login page with language set
      window.location.href = authUrl;
    }
  }
  
  if (isLoading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!user) {
    return <div className="loading">Redirecionando para login...</div>;
  }

  return (
    <div className="App">
      <Header />
      <div className="main-container">
        <Sidebar />
        <div className="content">
          <Routes>
            <Route path="/" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.dashboard}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/home" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.dashboard}>
                <Dashboard />
              </ProtectedRoute>
            } />
            {/* <Route path="/protocolo" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.protocolo}>
                <Procedimentos />
              </ProtectedRoute>
            } /> */}
            <Route path="/agenda/ver" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.agenda}>
                <div className="agenda"><BookingViewPage /></div>
              </ProtectedRoute>
            } />
            <Route path="/agenda/criar" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.agenda}>
                <div className="agenda"><BookingCreatePage /></div>
              </ProtectedRoute>
            } />
            <Route path="/agenda/bloqueios" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.agenda}>
                <div className="agenda"><BlockingPage /></div>
              </ProtectedRoute>
            } />
            <Route path="/agenda/relatorio" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.agenda}>
                <AgendamentosReportPage />
              </ProtectedRoute>
            } />
            {/* <Route path="/historico" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.historico}>
                <div className="historico"><Historico/></div>
              </ProtectedRoute>
            } /> */}
            <Route path="/anamnese" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.anamnese}>
                <Anamnese />
              </ProtectedRoute>
            } />
            <Route path="/pacientes" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.pacientes}>
                <Pacientes />
              </ProtectedRoute>
            } />
            <Route path="/config" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.config}>
                <Config />
              </ProtectedRoute>
            } />
            <Route path="/financeiro" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.financeiro}>
                <Financeiro />
              </ProtectedRoute>
            } />
            <Route path="/fluxo-caixa" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.cashflow}>
                <CashFlow />
              </ProtectedRoute>
            } />
            {/* <Route path="/report" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.financeiro}>
                <ReportPage />
              </ProtectedRoute>
            } /> */}
            <Route path="/estoque" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.estoque}>
                <Estoque />
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.reports}>
                <Reports />
              </ProtectedRoute>
            } />
            {/* <Route path="/nexusai" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.nexusai}>
                <NexusAI />
              </ProtectedRoute>
            } /> */}
            {/* <Route path="/nexusai/:chatId" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.nexusai}>
                <NexusAI />
              </ProtectedRoute>
            } /> */}
            <Route path="/prontuario" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.prontuario}>
                <Prontuario />
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute allowedGroups={ROUTE_PERMISSIONS.users}>
                <Users />
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;